import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as PR from "../../prime-modules/index";
import AdminHeader from "../layout/admin-header";
import AdminFooter from "../layout/admin-footer";
import {
    getDeviceTypes,
    createDeviceType,
    updateDeviceType,
    deleteDeviceType
} from "../../services/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";
import "./DeviceTypes.scss";

// Compare two non-negative integer strings: returns >0 if a > b, <0 if a < b, 0 if equal
const compareNumericStrings = (a, b) => {
    const strA = String(a || "").replace(/\D/g, "").replace(/^0+/, "") || "0";
    const strB = String(b || "").replace(/\D/g, "").replace(/^0+/, "") || "0";
    if (strA.length !== strB.length) {
        return strA.length - strB.length;
    }
    return strA.localeCompare(strB);
};

const DeviceTypes = () => {
    const dispatch = useDispatch();
    const adminData = useSelector((state) => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    // Enable create device type for Super Admin only
    const isSuperAdmin = hasPermission(adminData?.permission, PERMISSIONS.MANAGE_ADMINS) || adminData?.role === 1;

    const toast = useRef(null);

    // List & loading state
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Search and filter state
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedGroupFilter, setSelectedGroupFilter] = useState("");

    // Modal dialog state
    const [deviceTypeDialog, setDeviceTypeDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [selectedDeviceType, setSelectedDeviceType] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // Fetch device types list (GET /v1/device-types)
    const fetchDeviceTypes = useCallback(() => {
        setLoading(true);
        getDeviceTypes(headers, dispatch, (response) => {
            setLoading(false);
            if (response.result === "SUCCESS") {
                const list = Array.isArray(response.data) ? response.data : [];
                setDeviceTypes(list);
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.error?.errorMsg || "Failed to fetch device types",
                    life: 3500
                });
            }
        });
    }, [dispatch, headers]);

    useEffect(() => {
        if (adminData.sessionId) {
            fetchDeviceTypes();
        }
    }, [adminData.sessionId, fetchDeviceTypes]);

    // Unique groups for filtering
    const groupOptions = useMemo(() => {
        const unique = Array.from(new Set(deviceTypes.map((d) => d.groupId).filter(Boolean)));
        return unique.map((g) => ({ label: g, value: g }));
    }, [deviceTypes]);

    // Filtered data based on search and group filter
    const filteredDeviceTypes = useMemo(() => {
        let result = deviceTypes;

        if (selectedGroupFilter) {
            result = result.filter((d) => d.groupId === selectedGroupFilter);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter((d) => {
                const nameMatch = d.name ? d.name.toLowerCase().includes(query) : false;
                const groupMatch = d.groupId ? d.groupId.toLowerCase().includes(query) : false;
                const idMatch = d.id !== undefined && d.id !== null ? String(d.id).includes(query) : false;
                return nameMatch || groupMatch || idMatch;
            });
        }

        return result;
    }, [deviceTypes, selectedGroupFilter, searchQuery]);

    // Stats calculations
    const stats = useMemo(() => {
        const totalCount = deviceTypes.length;
        const uniqueGroups = new Set(deviceTypes.map((d) => d.groupId).filter(Boolean)).size;
        const totalSequenced = deviceTypes.filter(
            (d) => Number(d.iccIdSeqEnd) >= Number(d.iccIdSeqStart) && Number(d.iccIdSeqEnd) > 0
        ).length;
        return { totalCount, uniqueGroups, totalSequenced };
    }, [deviceTypes]);

    // Formik setup for Create / Edit
    const formik = useFormik({
        initialValues: {
            name: "",
            groupId: "",
            iccIdSeqStart: "",
            iccIdSeqEnd: "",
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .trim()
                .required("Device type name is required")
                .min(2, "Name must be at least 2 characters"),
            groupId: Yup.string()
                .trim()
                .required("Group ID is required"),
            iccIdSeqStart: Yup.string()
                .trim()
                .required("ICCID sequence start is required")
                .matches(/^\d+$/, "ICCID sequence start must contain only digits")
                .min(18, "ICCID sequence start must be between 18 and 22 digits")
                .max(22, "ICCID sequence start must be between 18 and 22 digits")
                .test(
                    "is-less-or-equal",
                    "ICCID sequence start must be less than sequence end",
                    function (value) {
                        const { iccIdSeqEnd } = this.parent;
                        if (!value || !iccIdSeqEnd || !/^\d+$/.test(value) || !/^\d+$/.test(iccIdSeqEnd)) return true;
                        return compareNumericStrings(value, iccIdSeqEnd) <= 0;
                    }
                ),
            iccIdSeqEnd: Yup.string()
                .trim()
                .required("ICCID sequence end is required")
                .matches(/^\d+$/, "ICCID sequence end must contain only digits")
                .min(18, "ICCID sequence end must be between 18 and 22 digits")
                .max(22, "ICCID sequence end must be between 18 and 22 digits")
                .test(
                    "is-greater-or-equal",
                    "ICCID sequence end must be greater than or equal to sequence start",
                    function (value) {
                        const { iccIdSeqStart } = this.parent;
                        if (!value || !iccIdSeqStart || !/^\d+$/.test(value) || !/^\d+$/.test(iccIdSeqStart)) return true;
                        return compareNumericStrings(value, iccIdSeqStart) >= 0;
                    }
                ),
        }),
        onSubmit: (values) => {
            if (!isSuperAdmin) return;
            const deviceName = values.name.trim();
           const body = {
    name: deviceName,
    groupId: values.groupId.trim(),
    iccIdSeqStart: values.iccIdSeqStart?.trim() || "0",
    iccIdSeqEnd: values.iccIdSeqEnd?.trim() || "0",
    iconIndex: selectedDeviceType?.iconIndex ?? 0,
};

const payload = JSON.stringify(body).replace(
    /("(?:iccIdSeqStart|iccIdSeqEnd)"\s*:\s*)"(\d+)"/g,
    "$1$2"
);
            setActionLoading(true);

            if (isEditMode && selectedDeviceType?.id !== undefined) {
                // Update device type (PUT /v1/device-types/{id})
                updateDeviceType(selectedDeviceType.id, payload, headers, dispatch, (response) => {
                    setActionLoading(false);
                    if (response.result === "SUCCESS") {
                        toast.current?.show({
                            severity: "success",
                            summary: "Success",
                            detail: `Device type "${deviceName}" updated successfully`,
                            life: 3000
                        });
                        setDeviceTypeDialog(false);
                        fetchDeviceTypes();
                    } else {
                        toast.current?.show({
                            severity: "error",
                            summary: "Error",
                            detail: response.error?.errorMsg || "Failed to update device type",
                            life: 4000
                        });
                    }
                });
            } else {
                // Create device type (POST /v1/device-types)
                createDeviceType(payload, headers, dispatch, (response) => {
                    setActionLoading(false);
                    if (response.result === "SUCCESS") {
                        toast.current?.show({
                            severity: "success",
                            summary: "Success",
                            detail: `Device type "${deviceName}" created successfully`,
                            life: 3000
                        });
                        setDeviceTypeDialog(false);
                        fetchDeviceTypes();
                    } else {
                        toast.current?.show({
                            severity: "error",
                            summary: "Error",
                            detail: response.error?.errorMsg || "Failed to create device type",
                            life: 4000
                        });
                    }
                });
            }
        }
    });

    // Open dialog for new device type
    const openCreateDialog = () => {
        if (!isSuperAdmin) return;
        setIsEditMode(false);
        setSelectedDeviceType(null);
        formik.resetForm({
            values: {
                name: "",
                groupId: "",
                iccIdSeqStart: "",
                iccIdSeqEnd: "",
            }
        });
        setDeviceTypeDialog(true);
    };

    // Open dialog for editing device type
    const openEditDialog = (item) => {
        if (!isSuperAdmin) return;
        setIsEditMode(true);
        setSelectedDeviceType(item);
        formik.resetForm({
            values: {
                name: item.name || "",
                groupId: item.groupId || "",
                iccIdSeqStart: item.iccIdSeqStart !== undefined && item.iccIdSeqStart !== null ? String(item.iccIdSeqStart) : "",
                iccIdSeqEnd: item.iccIdSeqEnd !== undefined && item.iccIdSeqEnd !== null ? String(item.iccIdSeqEnd) : "",
            }
        });
        setDeviceTypeDialog(true);
    };

    // Open delete confirmation
    const confirmDelete = (item) => {
        if (!isSuperAdmin) return;
        setSelectedDeviceType(item);
        setDeleteDialog(true);
    };

    // Confirm deletion (DELETE /v1/device-types/{id})
    const handleDeleteDeviceType = () => {
        if (!isSuperAdmin) return;
        if (!selectedDeviceType || selectedDeviceType.id === undefined) return;

        setDeleteLoading(true);
        deleteDeviceType(selectedDeviceType.id, headers, dispatch, (response) => {
            setDeleteLoading(false);
            setDeleteDialog(false);
            if (response.result === "SUCCESS") {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Device type "${selectedDeviceType.name}" removed successfully`,
                    life: 3000
                });
                setSelectedDeviceType(null);
                fetchDeviceTypes();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.error?.errorMsg || "Failed to delete device type",
                    life: 4000
                });
            }
        });
    };

    const resetFilters = () => {
        setSearchQuery("");
        setSelectedGroupFilter("");
    };

    // Column templates
    const idTemplate = (rowData) => (
        <span className="id-badge">#{rowData.id}</span>
    );

    const nameTemplate = (rowData) => (
        <div className="device-name-cell">
            <div className="device-avatar">
                <i className="pi pi-mobile" />
            </div>
            <div className="device-info">
                <span className="device-name">{rowData.name}</span>
            </div>
        </div>
    );

    const groupTemplate = (rowData) => (
        <span className="group-badge">
            <i className="pi pi-tag" style={{ fontSize: "10px" }} />
            {rowData.groupId}
        </span>
    );

    const sequenceTemplate = (rowData) => {
        const start = rowData.iccIdSeqStart !== undefined && rowData.iccIdSeqStart !== null ? String(rowData.iccIdSeqStart) : "0";
        const end = rowData.iccIdSeqEnd !== undefined && rowData.iccIdSeqEnd !== null ? String(rowData.iccIdSeqEnd) : "0";

        let count = "";
        if (typeof window !== "undefined" && window.BigInt) {
            try {
                const bStart = window.BigInt(start);
                const bEnd = window.BigInt(end);
                const bOne = window.BigInt(1);
                const bZero = window.BigInt(0);
                if (bEnd >= bStart && bEnd > bZero) {
                    count = (bEnd - bStart + bOne).toString();
                }
            } catch (e) {
                count = "";
            }
        } else {
            const numStart = Number(start);
            const numEnd = Number(end);
            if (!isNaN(numStart) && !isNaN(numEnd) && numEnd >= numStart && numEnd > 0) {
                const diff = numEnd - numStart + 1;
                count = diff > 0 ? diff.toLocaleString() : "";
            }
        }

        return (
            <div className="sequence-range-badge">
                <span className="seq-num">{start}</span>
                <i className="pi pi-arrow-right seq-arrow" />
                <span className="seq-num">{end}</span>
                {count && <span className="seq-count">({count} ICCIDs)</span>}
            </div>
        );
    };

    const actionTemplate = (rowData) => {
        if (!isSuperAdmin) return null;
        return (
            <div className="action-buttons">
                <PR.Button
                    icon="pi pi-pencil"
                    rounded
                    outlined
                    className="mr-2 icon-button"
                    onClick={() => openEditDialog(rowData)}
                />
                <PR.Button
                    icon="pi pi-trash"
                    rounded
                    outlined
                    severity="danger"
                    className="icon-button delete"
                    onClick={() => confirmDelete(rowData)}
                />
            </div>
        );
    };

    return (
        <div className="main">
            <div className="layout-sidebar">
                <AdminHeader />
            </div>
            <div className="layout-content-wrapper relative">
                <section className="device-types-container">
                    {/* Header */}
                    <div className="flex flex-column sm:flex-row align-items-start sm:align-items-center justify-content-between gap-3 mb-4">
                        <div className="heading-sec">
                            <h1>Device Types</h1>
                        </div>
                        <div className="header-actions">
                            {isSuperAdmin && (
                                <PR.Button
                                    label="Create Device Type"
                                    icon="pi pi-plus"
                                    className="create-button"
                                    onClick={openCreateDialog}
                                />
                            )}
                        </div>
                    </div>
 
                    {/* Filter & Search Bar */}
                    <div className="filter-card">
                        <div className="flex flex-column md:flex-row align-items-stretch md:align-items-center justify-content-between gap-3">
                            <div className="search-input-wrapper">
                                <span className="p-input-icon-left w-full">
                                    <i className="pi pi-search" />
                                    <PR.InputText
                                        placeholder="Search by name, group ID, or ID..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="search-input"
                                    />
                                </span>
                            </div>

                            <div className="flex align-items-center gap-2 flex-wrap">
                                <PR.Dropdown
                                    value={selectedGroupFilter}
                                    options={groupOptions}
                                    onChange={(e) => setSelectedGroupFilter(e.value)}
                                    placeholder="Filter by Group"
                                    className="group-filter-dropdown"
                                />

                                {(searchQuery || selectedGroupFilter) && (
                                    <PR.Button
                                        label="Reset"
                                        icon="pi pi-filter-slash"
                                        className="reset-filter-btn"
                                        onClick={resetFilters}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Device Types Table */}
                    <div className="table-card">
                        <PR.DataTable
                            value={filteredDeviceTypes}
                            loading={loading}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} device types"
                            emptyMessage={
                                <div className="text-center p-4">
                                    <i className="pi pi-inbox text-4xl text-400 mb-2 block" />
                                    <span className="text-600 font-semibold">No device types found</span>
                                </div>
                            }
                            responsiveLayout="scroll"
                        >
                            <PR.Column field="name" header="Device Type" body={nameTemplate} sortable style={{ minWidth: "180px" }} />
                            <PR.Column field="groupId" header="Group ID" body={groupTemplate} sortable style={{ minWidth: "140px" }} />
                            <PR.Column field="iccIdSeqStart" header="ICCID Sequence Range" body={sequenceTemplate} sortable style={{ minWidth: "220px" }} />
                            {isSuperAdmin && (
                                <PR.Column header="Actions" body={actionTemplate} style={{ width: "110px", textAlign: "right" }} />
                            )}
                        </PR.DataTable>
                    </div>

                    {/* Create / Edit Dialog */}
                    <PR.Dialog
                        visible={isSuperAdmin && deviceTypeDialog}
                        header={isEditMode ? `Edit Device Type (ID: #${selectedDeviceType?.id})` : "Create New Device Type"}
                        className="device-type-dialog"
                        draggable={false}
                        resizable={false}
                        modal
                        onHide={() => setDeviceTypeDialog(false)}
                    >
                        <form onSubmit={formik.handleSubmit} autoComplete="off">
                            <div className="field">
                                <label htmlFor="name">
                                    Device Type Name <span className="required-star">*</span>
                                </label>
                                <PR.InputText
                                    id="name"
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={formik.errors.name && formik.touched.name ? "p-invalid" : ""}
                                />
                                {formik.errors.name && formik.touched.name && (
                                    <small className="p-error block mt-1">{formik.errors.name}</small>
                                )}
                            </div>

                            <div className="field">
                                <label htmlFor="groupId">
                                    Group ID <span className="required-star">*</span>
                                </label>
                                <PR.InputText
                                    id="groupId"
                                    name="groupId"
                                    value={formik.values.groupId}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={formik.errors.groupId && formik.touched.groupId ? "p-invalid" : ""}
                                />
                                {formik.errors.groupId && formik.touched.groupId && (
                                    <small className="p-error block mt-1">{formik.errors.groupId}</small>
                                )}
                            </div>

                            <div className="grid grid-nogutter gap-3">
                                <div className="col field" style={{ marginBottom: 0 }}>
                                    <label htmlFor="iccIdSeqStart">
                                        ICCID Sequence Start <span className="required-star">*</span>
                                    </label>
                                    <PR.InputText
                                        id="iccIdSeqStart"
                                        name="iccIdSeqStart"
                                        value={formik.values.iccIdSeqStart}
                                        maxLength={22}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, "").slice(0, 22);
                                            formik.setFieldValue("iccIdSeqStart", val);
                                        }}
                                        onBlur={formik.handleBlur}
                                        placeholder="e.g. 8100000000000000000"
                                        className={formik.errors.iccIdSeqStart && formik.touched.iccIdSeqStart ? "p-invalid" : ""}
                                    />
                                    {formik.errors.iccIdSeqStart && formik.touched.iccIdSeqStart && (
                                        <small className="p-error block mt-1">{formik.errors.iccIdSeqStart}</small>
                                    )}
                                </div>

                                <div className="col field" style={{ marginBottom: 0 }}>
                                    <label htmlFor="iccIdSeqEnd">
                                        ICCID Sequence End <span className="required-star">*</span>
                                    </label>
                                    <PR.InputText
                                        id="iccIdSeqEnd"
                                        name="iccIdSeqEnd"
                                        value={formik.values.iccIdSeqEnd}
                                        maxLength={22}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, "").slice(0, 22);
                                            formik.setFieldValue("iccIdSeqEnd", val);
                                        }}
                                        onBlur={formik.handleBlur}
                                        placeholder="e.g. 8100000000000000199"
                                        className={formik.errors.iccIdSeqEnd && formik.touched.iccIdSeqEnd ? "p-invalid" : ""}
                                    />
                                    {formik.errors.iccIdSeqEnd && formik.touched.iccIdSeqEnd && (
                                        <small className="p-error block mt-1">{formik.errors.iccIdSeqEnd}</small>
                                    )}
                                </div>
                            </div>

                            <div className="dialog-footer">
                                <PR.Button
                                    label="Cancel"
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setDeviceTypeDialog(false)}
                                    disabled={actionLoading}
                                />
                                <PR.Button
                                    label={isEditMode ? "Update Device Type" : "Create Device Type"}
                                    type="submit"
                                    className="save-btn"
                                    loading={actionLoading}
                                />
                            </div>
                        </form>
                    </PR.Dialog>

                    {/* Delete Confirmation Dialog */}
                    <PR.Dialog
                        visible={isSuperAdmin && deleteDialog}
                        header="Confirm Deletion"
                        className="delete-device-dialog"
                        draggable={false}
                        resizable={false}
                        modal
                        onHide={() => setDeleteDialog(false)}
                    >
                        <div className="delete-dialog-body">
                            <div className="warning-icon">
                                <i className="pi pi-exclamation-triangle" />
                            </div>
                            <div className="warning-content">
                                <h4>Delete this device type?</h4>
                                <p>
                                    Are you sure you want to permanently delete this device type? This operation cannot be undone.
                                </p>
                            </div>
                        </div>

                        <div className="dialog-footer">
                            <PR.Button
                                label="Cancel"
                                type="button"
                                className="cancel-btn"
                                onClick={() => setDeleteDialog(false)}
                                disabled={deleteLoading}
                            />
                            <PR.Button
                                label="Delete Device Type"
                                icon="pi pi-trash"
                                type="button"
                                className="confirm-delete-btn"
                                onClick={handleDeleteDeviceType}
                                loading={deleteLoading}
                            />
                        </div>
                    </PR.Dialog>
                </section>
                <AdminFooter />
            </div>
            <PR.Toast ref={toast} />
        </div>
    );
};

export default DeviceTypes;
